'use client';

import { useState, Fragment } from 'react';
import { apiClient } from '@/app/api/client';
import { Dialog, Transition } from '@headlessui/react';
import { CheckCircleIcon, ExclamationTriangleIcon, InformationCircleIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import { Button } from '@/components/forms';

export interface DisconnectLog {
  id: string;
  timestamp: Date;
  level: 'info' | 'success' | 'error' | 'warning';
  message: string;
  details?: string;
}

interface Connection {
  id: string;
  platformName: string;
  platformType: string;
  iconUrl: string;
  settings?: any;
}

interface DisconnectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
  connection: Connection | null;
}

export default function DisconnectionModal({ isOpen, onClose, onComplete, connection }: DisconnectionModalProps) {
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [disconnectLogs, setDisconnectLogs] = useState<DisconnectLog[]>([]);
  const [copySuccess, setCopySuccess] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);

  const addLog = (level: DisconnectLog['level'], message: string, details?: string) => {
    const log: DisconnectLog = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(),
      level,
      message,
      details
    };
    setDisconnectLogs(prev => [...prev, log]);
  };

  const copyLogsToClipboard = async () => {
    const logText = disconnectLogs.map(log => {
      const timestamp = log.timestamp.toLocaleString();
      const level = log.level.toUpperCase();
      let text = `[${timestamp}] ${level}: ${log.message}`;
      if (log.details) {
        text += `\n  Details: ${log.details}`;
      }
      return text;
    }).join('\n');

    try {
      await navigator.clipboard.writeText(logText);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy logs:', err);
    }
  };

  const handleDisconnect = async () => {
    if (!connection) return;
    
    setIsDisconnecting(true);
    setHasStarted(true);
    setDisconnectLogs([]);
    
    addLog('info', `Starting disconnection from ${connection.platformName}...`);
    
    let hasErrors = false;
    
    try {
      // Step 1: For BlogSpot connections, revoke the token first
      if (connection.platformType === 'blogspot') {
        addLog('info', 'BlogSpot connection detected, revoking OAuth tokens...');
        
        try {
          // Get the connection details to access the refresh token
          addLog('info', 'Fetching connection details...');
          const connectionDetails = await apiClient.get(`/api/connections`);
          const fullConnection = connectionDetails.data.find((c: any) => c.id === connection.id);
          
          if (fullConnection?.settings?.refreshToken) {
            addLog('info', 'Refresh token found, revoking with Google...');
            
            // Use POST method with form data in body instead of query params
            const revokeResponse = await fetch('https://oauth2.googleapis.com/revoke', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
              },
              body: `token=${encodeURIComponent(fullConnection.settings.refreshToken)}`
            });
            
            if (revokeResponse.ok) {
              addLog('success', 'OAuth token successfully revoked with Google');
            } else {
              const errorText = await revokeResponse.text();
              try {
                const errorData = JSON.parse(errorText);
                if (errorData.error === 'invalid_token' && errorData.error_description?.includes('not revocable')) {
                  addLog('warning', 'Token is not revocable (already expired or revoked)');
                  hasErrors = true;
                } else {
                  addLog('error', `Token revocation failed: ${revokeResponse.status}`, errorText);
                  hasErrors = true;
                }
              } catch {
                addLog('error', `Token revocation failed: ${revokeResponse.status}`, errorText);
                hasErrors = true;
              }
            }
          } else {
            addLog('warning', 'No refresh token found in connection settings');
            hasErrors = true;
          }
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error';
          addLog('error', 'Failed to revoke OAuth token', errorMessage);
          hasErrors = true;
        }
      }
      
      // Step 2: Delete the connection from backend
      addLog('info', 'Removing connection from database...');
      try {
        await apiClient.delete(`/api/connections/${connection.id}`);
        addLog('success', 'Connection successfully removed from database');
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        addLog('error', 'Failed to remove connection from database', errorMessage);
        hasErrors = true;
        setIsDisconnecting(false);
        return;
      }
      
      // Final result
      if (hasErrors) {
        addLog('warning', `Disconnection completed with warnings. Connection removed but some cleanup steps failed.`);
      } else {
        addLog('success', `Successfully disconnected from ${connection.platformName}!`);
      }
      
      // Wait a moment before completing
      setTimeout(() => {
        onComplete();
        setIsDisconnecting(false);
        setHasStarted(false);
      }, 1500);
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      addLog('error', 'Disconnection failed', errorMessage);
      setIsDisconnecting(false);
    }
  };

  const handleClose = () => {
    setDisconnectLogs([]);
    setHasStarted(false);
    setIsDisconnecting(false);
    onClose();
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10 focus:outline-none" onClose={() => {}}>
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        <div className="fixed inset-0 w-screen overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg rounded-xl bg-white p-6 shadow-2xl">
                <div className="flex items-center justify-between mb-6">
                  <Dialog.Title as="h3" className="text-lg font-medium text-gray-900">
                    {hasStarted 
                      ? `Disconnecting ${connection?.platformName || 'Connection'}`
                      : `Disconnect ${connection?.platformName || 'Connection'}`
                    }
                  </Dialog.Title>
                  {hasStarted && (
                    <button
                      onClick={copyLogsToClipboard}
                      disabled={disconnectLogs.length === 0}
                      className={`p-2 rounded-md transition-colors ${
                        disconnectLogs.length === 0
                          ? 'text-gray-300 cursor-not-allowed'
                          : copySuccess
                          ? 'text-green-600 bg-green-50'
                          : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                      }`}
                      title="Copy logs to clipboard"
                    >
                      <ClipboardDocumentIcon className="h-5 w-5" />
                    </button>
                  )}
                </div>
                
                {hasStarted ? (
                  <div className="bg-gray-50 rounded-lg p-4 h-96 overflow-y-auto">
                    {disconnectLogs.length === 0 ? (
                      <div className="text-gray-500 text-center py-8">
                        {isDisconnecting ? 'Starting disconnection...' : 'No logs yet'}
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {disconnectLogs.map((log) => (
                          <div key={log.id} className="flex items-start space-x-2">
                            <div className="flex-shrink-0 mt-0.5">
                              {log.level === 'success' && <CheckCircleIcon className="h-4 w-4 text-green-500" />}
                              {log.level === 'error' && <ExclamationTriangleIcon className="h-4 w-4 text-red-500" />}
                              {log.level === 'warning' && <ExclamationTriangleIcon className="h-4 w-4 text-yellow-500" />}
                              {log.level === 'info' && <InformationCircleIcon className="h-4 w-4 text-blue-500" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center space-x-2">
                                <span className="text-xs text-gray-500">
                                  {log.timestamp.toLocaleTimeString()}
                                </span>
                                <span className={`text-sm ${
                                  log.level === 'success' ? 'text-green-700' :
                                  log.level === 'error' ? 'text-red-700' :
                                  log.level === 'warning' ? 'text-yellow-700' :
                                  'text-gray-700'
                                }`}>
                                  {log.message}
                                </span>
                              </div>
                              {log.details && (
                                <div className="mt-1 text-xs text-gray-600 bg-white rounded px-2 py-1">
                                  {log.details}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {isDisconnecting && (
                      <div className="flex items-center space-x-2 mt-4 p-2 bg-blue-50 rounded">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                        <span className="text-sm text-blue-700">Disconnecting in progress...</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="flex items-center justify-center mb-4">
                      {connection?.iconUrl && (
                        <img src={connection.iconUrl} alt={connection.platformName} className="w-12 h-12 mr-3" />
                      )}
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900">{connection?.platformName}</h4>
                        <p className="text-sm text-gray-500">Ready to disconnect</p>
                      </div>
                    </div>
                    <p className="text-gray-600 mb-6">
                      This will revoke all access and remove the connection. This action cannot be undone.
                    </p>
                  </div>
                )}

                <div className="mt-8 flex justify-between items-center">
                  {hasStarted ? (
                    <div className="flex gap-4 w-full justify-end">
                      <Button 
                        onClick={handleClose}
                        disabled={isDisconnecting}
                      >
                        {isDisconnecting ? 'Disconnecting...' : 'Close'}
                      </Button>
                    </div>
                  ) : (
                    <div className="flex gap-4 w-full justify-between">
                      <Button plain onClick={handleClose}>Cancel</Button>
                      <Button 
                        onClick={handleDisconnect} 
                        color="red"
                        disabled={isDisconnecting}
                      >
                        Start Disconnection
                      </Button>
                    </div>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
